
import { useState, useEffect, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

type Profile = Tables<'profiles'> & {
  avatar_url?: string | null;
  display_name?: string | null;
};

export function useSupabaseAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId: string, userForFallback?: User) => {
    try {
      // Récupérer le profil de la base de données
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        
        // En cas d'erreur, essayons de vérifier si l'utilisateur existe dans auth.users
        const userData = userForFallback?.user_metadata;
        if (userData) {
          // Créer un profil temporaire basé sur les metadonnées de l'utilisateur
          setProfile({
            id: userId,
            first_name: userData.first_name,
            last_name: userData.last_name,
            role: userData.role || 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            avatar_url: null,          // Ajout de la propriété manquante
            display_name: null         // Ajout de la propriété manquante
          });
        }
        
        return;
      }

      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  }, []);

  // Auth state: only sync session/user, no async calls (avoids deadlock per Supabase docs)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (!currentSession?.user) {
          setProfile(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Profile fetch: separate effect to avoid async in onAuthStateChange (prevents deadlock)
  useEffect(() => {
    if (!user?.id) {
      setProfile(null);
      return;
    }
    fetchProfile(user.id, user);
  }, [user?.id, fetchProfile]);

  return {
    session,
    user,
    profile,
    loading,
    fetchProfile,
    isAdmin: profile?.role === 'admin',
    isProfessional: profile?.role === 'professional',
  };
}
